/**
 * Partner Broadcast body markup helpers (Jun 2026).
 *
 * The body is stored as plain text with a deliberately tiny vocabulary —
 * **bold**, *italic*, `- ` bullets and [label](url) — which the backend
 * turns into email-safe HTML. Rich text pasted from Word, Google Docs or
 * an email client is converted DOWN into that vocabulary: the meaning
 * (bold, italic, lists, links) is kept, and fonts, colours, sizes and
 * Word's markup soup are discarded so nothing can break Outlook or
 * override the AWARE brand styling.
 *
 * Headings are intentionally converted to bold rather than preserved —
 * broadcasts stay visually consistent across partners.
 */

const BOLD_TAGS = new Set(["STRONG", "B", "H1", "H2", "H3", "H4", "H5", "H6", "TH"]);
const ITALIC_TAGS = new Set(["EM", "I"]);
const BLOCK_TAGS = new Set([
  "P", "DIV", "SECTION", "ARTICLE", "HEADER", "FOOTER", "BLOCKQUOTE",
  "H1", "H2", "H3", "H4", "H5", "H6", "TR", "TABLE", "UL", "OL", "PRE",
]);
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "HEAD", "META", "LINK", "NOSCRIPT"]);

const isBold = (el) => {
  if (BOLD_TAGS.has(el.tagName)) return true;
  const weight = el.style?.fontWeight || "";
  if (weight === "bold" || weight === "bolder") return true;
  const numeric = parseInt(weight, 10);
  return Number.isFinite(numeric) && numeric >= 600;
};

const isItalic = (el) =>
  ITALIC_TAGS.has(el.tagName) || el.style?.fontStyle === "italic";

/** Don't emit ** around whitespace — "** bold **" isn't valid markup. */
const wrap = (text, marker) => {
  const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(text);
  if (!match || !match[2]) return text;
  return `${match[1]}${marker}${match[2]}${marker}${match[3]}`;
};

/** Escape characters that would otherwise be read as our own markup. */
const escapeMarkers = (text) => text.replace(/([*_])/g, "\\$1");

function walk(node, ctx) {
  if (node.nodeType === Node.TEXT_NODE) {
    // Collapse HTML whitespace the way a browser would render it.
    return escapeMarkers(node.nodeValue.replace(/\s+/g, " "));
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node;
  if (SKIP_TAGS.has(el.tagName)) return "";

  if (el.tagName === "BR") return "\n";

  if (el.tagName === "LI") {
    const inner = childText(el, ctx).replace(/\s+/g, " ").trim();
    return inner ? `\n- ${inner}` : "";
  }

  if (el.tagName === "A") {
    const label = childText(el, ctx).replace(/\s+/g, " ").trim();
    const href = (el.getAttribute("href") || "").trim();
    if (!label) return "";
    // Only http(s). mailto/js/relative links are flattened to their text.
    if (!/^https?:\/\//i.test(href)) return label;
    // A link whose label already IS the url stays bare.
    if (label === href) return href;
    return `[${label}](${href})`;
  }

  let text = childText(el, ctx);
  if (!text.trim()) return BLOCK_TAGS.has(el.tagName) ? "\n\n" : "";

  if (isItalic(el)) text = wrap(text, "*");
  if (isBold(el)) text = wrap(text, "**");

  if (BLOCK_TAGS.has(el.tagName)) return `\n\n${text}\n\n`;
  return text;
}

function childText(el, ctx) {
  let out = "";
  el.childNodes.forEach((child) => {
    out += walk(child, ctx);
  });
  return out;
}

/** Rich-text HTML → the broadcast body vocabulary. */
export function htmlToBroadcastMarkup(htmlString) {
  if (!htmlString) return "";
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  let text = childText(doc.body, {});
  return text
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\*\*\s*\*\*/g, "")
    .replace(/(^|\n)- +/g, "$1- ")
    .trim();
}

/**
 * Expand a selection so it never cuts through a {{merge_field}}.
 * Without this, bolding across a token produces "{{first_na**me}}",
 * which would be stripped at send time and leave a broken greeting.
 */
export function snapOutsideTokens(value, start, end) {
  let s = start;
  let e = end;
  const re = /\{\{[^{}]*\}\}/g;
  let m;
  while ((m = re.exec(value)) !== null) {
    const from = m.index;
    const to = m.index + m[0].length;
    if (start > from && start < to) s = Math.min(s, from);
    if (end > from && end < to) e = Math.max(e, to);
  }
  return [s, e];
}

/**
 * Toggle a marker around the current selection in a textarea.
 * Returns the next value plus where the caret should land.
 */
export function toggleMarker(value, start, end, marker) {
  const selected = value.slice(start, end);
  const len = marker.length;
  const before = value.slice(Math.max(0, start - len), start);
  const after = value.slice(end, end + len);

  // Already wrapped → unwrap.
  if (before === marker && after === marker) {
    return {
      value: value.slice(0, start - len) + selected + value.slice(end + len),
      selectionStart: start - len,
      selectionEnd: end - len,
    };
  }
  if (selected.startsWith(marker) && selected.endsWith(marker) &&
      selected.length > len * 2) {
    const inner = selected.slice(len, -len);
    return {
      value: value.slice(0, start) + inner + value.slice(end),
      selectionStart: start,
      selectionEnd: start + inner.length,
    };
  }
  // Nothing selected → drop in the markers and put the caret between.
  if (!selected) {
    return {
      value: value.slice(0, start) + marker + marker + value.slice(end),
      selectionStart: start + len,
      selectionEnd: start + len,
    };
  }
  return {
    value: value.slice(0, start) + marker + selected + marker + value.slice(end),
    selectionStart: start + len,
    selectionEnd: end + len,
  };
}

/** Prefix every selected line with "- ", or strip it if already bulleted. */
export function toggleBullets(value, start, end) {
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const nextBreak = value.indexOf("\n", end);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  const block = value.slice(lineStart, lineEnd);
  const lines = block.split("\n");
  const allBulleted = lines.every((l) => !l.trim() || /^\s*[-*] /.test(l));
  const next = lines
    .map((l) => {
      if (!l.trim()) return l;
      return allBulleted ? l.replace(/^\s*[-*] /, "") : `- ${l.trim()}`;
    })
    .join("\n");
  return {
    value: value.slice(0, lineStart) + next + value.slice(lineEnd),
    selectionStart: lineStart,
    selectionEnd: lineStart + next.length,
  };
}

/** Wrap the selection as a [label](url) link. */
export function insertLink(value, start, end, url) {
  const label = value.slice(start, end) || "link text";
  const snippet = `[${label}](${url})`;
  return {
    value: value.slice(0, start) + snippet + value.slice(end),
    selectionStart: start + 1,
    selectionEnd: start + 1 + label.length,
  };
}
